// ============================================================================
//   MENU CATALOG — estructura del menú lateral, en el FRONTEND
// ============================================================================
// Fuente de la ESTRUCTURA del menú (secciones, labels, íconos, rutas) y del
// CÓDIGO de permiso que cada ítem requiere. El menú se renderiza filtrando este
// catálogo por los permisos del usuario (`settings.permissions`), NO leyendo un
// menú ya armado por el backend (`settings.accesses`, ahora legacy).
//
// Espejo de `backend/access_catalog.py` (ACCESS_CATALOG → menuItems). Mantener en
// sync: si el backend agrega/renombra una funcionalidad con menú, reflejarlo aquí
// (ver la deuda técnica "menú por códigos" — la estructura vive en dos lugares
// hasta que el backend exponga el catálogo o se genere en build-time).
//
// Notas de paridad con el backend:
//   - Orden de secciones e ítems = orden de `ACCESS_CATALOG`.
//   - `documentSign` (Firmar PDF) SÍ se muestra a quien tiene el permiso: es una pantalla
//     que los usuarios usan. Lo que no se hace es publicitarlo en el sitio de marketing.
//   - `upgradeWhen`/`upgradeTo`: si el usuario también tiene el code de `upgradeWhen`,
//     el ítem apunta a `upgradeTo` (destino completo) en vez de `to` (restringido).
//   - Contabilidad va gated por el permiso 'accounting' + el flag `accountingActive`, salvo
//     "Apertura / Estado", que es donde se activa el módulo (ver la sección).

export const MENU_CATALOG = [
    {
        label: 'Inicio',
        items: [
            { label: 'Panel de control', icon: 'pi pi-fw pi-chart-bar', to: '/dashboard', code: 'dashboard' },
        ],
    },
    {
        label: 'Cuenta',
        items: [
            { label: 'Negocio', icon: 'pi pi-fw pi-building', to: '/dashboard/general/informacion-basica', code: 'business' },
            { label: 'Suscripción', icon: 'pi pi-fw pi-credit-card', to: '/dashboard/general/suscripcion', code: 'subscription' },
            // Gestión de operadores (sub-usuarios): su propio ítem, no dentro de Configuración.
            // Gated por 'configuration' (solo el dueño/admin; el operador no tiene ese código).
            { label: 'Usuarios', icon: 'pi pi-fw pi-users', to: '/dashboard/general/usuarios', code: 'configuration' },
            { label: 'Configuración', icon: 'pi pi-fw pi-cog', to: '/dashboard/general/configuracion', code: 'configuration' },
            // Horarios de los meseros (F5 del cliente móvil). No llega solo a nadie: se habilita por
            // negocio desde la consola, y un Operador nunca lo tiene.
            { label: 'Horarios', icon: 'pi pi-fw pi-clock', to: '/dashboard/general/horarios', code: 'workSchedule' },
        ],
    },
    {
        label: 'General',
        items: [
            { label: 'Clientes', icon: 'pi pi-fw pi-id-card', to: '/dashboard/cliente/crud', code: 'customers' },
            { label: 'Proveedores', icon: 'pi pi-fw pi-sitemap', to: '/dashboard/proveedor/crud', code: 'providers' },
            { label: 'Firmar PDF', icon: 'pi pi-fw pi-file-pdf', to: '/dashboard/documentos/firmar-pdf', code: 'documentSign' },
        ],
    },
    {
        label: 'Productos/Servicios',
        items: [
            { label: 'Productos', icon: 'pi pi-fw pi-box', to: '/dashboard/producto/crud', code: 'products' },
            { label: 'Kardex', icon: 'pi pi-fw pi-book', to: '/dashboard/producto/kardex', code: 'kardex' },
            // El operador cuenta a ciegas; con inventoryManage el destino es el historial de conteos.
            { label: 'Conteo de inventario', icon: 'pi pi-fw pi-check-square', to: '/dashboard/inventario/conteo', upgradeTo: '/dashboard/inventario/conteo/historial', upgradeWhen: 'inventoryManage', code: 'inventoryCount' },
            { label: 'Sincronizar stock', icon: 'pi pi-fw pi-sync', to: '/dashboard/inventario/sincronizar', code: 'stockSync' },
            { label: 'Ajustes de inventario', icon: 'pi pi-fw pi-chart-line', to: '/dashboard/inventario/ajustes', code: 'inventoryManage' },
        ],
    },
    {
        label: 'Finanzas',
        items: [
            { label: 'Balance', icon: 'pi pi-fw pi-list', to: '/dashboard/balance-saldo', code: 'balance' },
            { label: 'Cierre de caja', icon: 'pi pi-fw pi-list', to: '/dashboard/cierre-caja', upgradeTo: '/dashboard/cierre-caja/historial', upgradeWhen: 'cashClosingManage', code: 'cashClosing' },
            { label: 'Cuentas', icon: 'pi pi-fw pi-money-bill', to: '/dashboard/cuenta/crud', code: 'finantialAccounts' },
            { label: 'Cobros y Pagos', icon: 'pi pi-fw pi-wallet', to: '/dashboard/cuentas-por-cobrar-pagar', code: 'receivablesPayables' },
            { label: 'Proformas', icon: 'pi pi-fw pi-file', to: '/dashboard/proforma/crud', code: 'proformas' },
            { label: 'Egresos', icon: 'pi pi-fw pi-shopping-bag', to: '/dashboard/compra/form', upgradeTo: '/dashboard/compra/crud', upgradeWhen: 'purchasesManage', code: 'purchases' },
            // El Salón (giro restaurante) y Ventas son pantallas separadas: el Salón muestra las ventas
            // abiertas por mesa y zona; Ventas, todas. Cada una con su permiso, para habilitarlas por
            // separado (un mesero puede tener solo el Salón).
            { label: 'Salón', icon: 'pi pi-fw pi-th-large', to: '/dashboard/salon', code: 'restaurantTables' },
            { label: 'Ventas', icon: 'pi pi-fw pi-shopping-cart', to: '/dashboard/venta/crud', code: 'sales' },
        ],
    },
    {
        label: 'Documentos Electrónicos',
        items: [
            { label: 'Guías de Remisión', icon: 'pi pi-fw pi-car', to: '/dashboard/guia-remision/crud', code: 'remisionGuides' },
            { label: 'Notas de Crédito', icon: 'pi pi-fw pi-sort-alt', to: '/dashboard/nota-credito/crud', code: 'creditNotes' },
        ],
    },
    {
        // Contabilidad: opt-in. Requiere el permiso 'accounting' Y que el módulo esté habilitado en
        // la actividad (`accountingActive`: AccountingConfig.enabled, el mismo gate de las vistas del
        // libro). Con `accountingEnabled`, que en legacy se ata a la compra, una cuenta nueva veía
        // estas 9 pantallas y todas respondían «no está habilitado» (H9). El gate por code hace que
        // desaparezca solo en un downgrade (sin tocar posting).
        // "Apertura / Estado" va SOLO con el permiso: es donde se activa el módulo, así que quien
        // lo compró tiene que poder llegar antes de activarlo.
        label: 'Contabilidad',
        items: [
            { label: 'Apertura / Estado', icon: 'pi pi-fw pi-power-off', to: '/dashboard/contabilidad/apertura', code: 'accounting' },
            { label: 'Estado de Resultados', icon: 'pi pi-fw pi-chart-line', to: '/dashboard/contabilidad/estado-resultados', code: 'accounting', requiresFlag: 'accountingActive' },
            { label: 'Balance General', icon: 'pi pi-fw pi-briefcase', to: '/dashboard/contabilidad/balance-general', code: 'accounting', requiresFlag: 'accountingActive' },
            { label: 'Balance de Comprobación', icon: 'pi pi-fw pi-check-square', to: '/dashboard/contabilidad/balance-comprobacion', code: 'accounting', requiresFlag: 'accountingActive' },
            { label: 'Plan de Cuentas', icon: 'pi pi-fw pi-sitemap', to: '/dashboard/contabilidad/plan-cuentas', code: 'accounting', requiresFlag: 'accountingActive' },
            { label: 'Mapeos', icon: 'pi pi-fw pi-link', to: '/dashboard/contabilidad/mapeos', code: 'accounting', requiresFlag: 'accountingActive' },
            { label: 'Activos Fijos', icon: 'pi pi-fw pi-building', to: '/dashboard/contabilidad/activos', code: 'accounting', requiresFlag: 'accountingActive' },
            { label: 'Liquidaciones', icon: 'pi pi-fw pi-wallet', to: '/dashboard/contabilidad/liquidaciones', code: 'accounting', requiresFlag: 'accountingActive' },
            { label: 'Resumen de IVA (104)', icon: 'pi pi-fw pi-percentage', to: '/dashboard/contabilidad/resumen-iva', code: 'accounting', requiresFlag: 'accountingActive' },
            { label: 'Conciliación Pasarelas', icon: 'pi pi-fw pi-sync', to: '/dashboard/contabilidad/conciliacion', code: 'accounting', requiresFlag: 'accountingActive' },
        ],
    },
];

// menuFlags(settings) -> flags de `buildMenuFromPermissions`, desde `user.settings` del backend.
//   - accountingEnabled: el usuario tiene la Contabilidad (en legacy, flag que se ata a la compra).
//   - accountingActive: además el módulo está habilitado en la actividad, que es lo que piden las
//     pantallas del libro. Un backend anterior no lo manda: se cae a `accountingEnabled`, como antes.
export function menuFlags(settings) {
    const s = settings || {};
    const enabled = !!s.accountingEnabled;
    const active = s.accountingActive === undefined ? enabled : (enabled && !!s.accountingActive);
    return { accountingEnabled: enabled, accountingActive: active };
}

// buildMenuFromPermissions(hasPerm, opts) -> [{label, items:[{label,icon,to}]}]
// Filtra MENU_CATALOG por los permisos del usuario. Puro (sin store): recibe la
// función `hasPerm(code) -> bool` para ser testeable y reutilizable.
//   - opts.flags: flags booleanos del negocio para ítems con `requiresFlag` (p.ej.
//     'accountingActive' para Contabilidad; ver `menuFlags`); si el flag no es truthy, el ítem se oculta.
//
//   - opts.visible: menú CONGELADO (`settings.menu`): array de rutas base (el `to` del catálogo)
//     que el usuario ve. Si es un array, solo se dibujan esos ítems (y siempre con permiso); si no
//     viene, se dibuja todo lo permitido. Lo escriben `freeze_legacy_menus` (los usuarios que ya
//     existían siguen viendo lo mismo que antes) y la consola de superadmin, que habilita las
//     pantallas nuevas una por una. No es seguridad: el router y el backend mandan por código.
//
// NOTA: la visibilidad del menú es por PERMISO (código). NO se usa `settings_headers` para
// ocultar ítems: su default per-usuario ("Cuentas": value=false) ocultaba un módulo que el
// usuario SÍ tiene concedido (a dueños y operadores por igual). El único opt-in que queda es
// Contabilidad, vía su flag `accountingActive`. Si en el futuro se quiere un "ocultar módulo
// opcional" por negocio, debe ser opt-OUT explícito a nivel de negocio, no un default per-usuario.
export function buildMenuFromPermissions(hasPerm, opts) {
    const flags = (opts && opts.flags) || {};
    const visible = (opts && Array.isArray(opts.visible)) ? opts.visible : null;
    const sections = [];
    for (const section of MENU_CATALOG) {
        const items = [];
        for (const item of section.items) {
            if (!hasPerm(item.code)) continue;
            // Menú congelado: se compara la ruta BASE, antes del upgrade de destino.
            if (visible && visible.indexOf(item.to) === -1) continue;
            // Gate por flag del negocio (p.ej. Contabilidad opt-in): oculto si el flag no está activo.
            if (item.requiresFlag && !flags[item.requiresFlag]) continue;
            // Destino: sube a `upgradeTo` si el usuario tiene el code de `upgradeWhen`.
            const to = (item.upgradeWhen && hasPerm(item.upgradeWhen)) ? item.upgradeTo : item.to;
            items.push({ label: item.label, icon: item.icon, to });
        }
        if (items.length) sections.push({ label: section.label, items });
    }
    return sections;
}
